import React, { useCallback, useEffect, useRef, useState } from "react"
import { useLocation, useSearchParams } from "react-router-dom"
import { AlertTriangle, Loader2, X } from "lucide-react"

import { useAppDispatch, useAppSelector } from "hooks/storeHooks"
import {
  closeBillingPaywall,
  openBillingPaywall,
  selectBillingEntitlement,
  selectBillingPaywallOpen,
  selectBillingPortalUrl,
  setBillingEntitlement,
  setBillingPortalUrl,
} from "store/billing/billingSlice"
import {
  type StripeCatalogOffer,
  type StripeCheckoutStatusState,
  type StripeErrorResponse,
} from "store/billing/types"
import { billingApi } from "services/billingApi"
import { capturePlatformEvent } from "services/platformAnalytics"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { cn } from "@/lib/utils"

type BillingCatalogStatus = "idle" | "loading" | "ready" | "error"

const STRIPE_CHECKOUT_SESSION_STORAGE_KEY = "tint.billing.checkout.session_id"
const BILLING_PORTAL_RETURN_QUERY_KEY = "billing_portal_return"
const STRIPE_RETURN_QUERY_KEYS = [
  "session_id",
  "checkout_session_id",
  "payment_intent",
  "redirect_status",
  "billing_cancelled",
  BILLING_PORTAL_RETURN_QUERY_KEY,
] as const
const CHECKOUT_STATUS_POLL_DELAYS_MS = [0, 1_500, 2_500, 4_000, 6_500, 10_000]
const PORTAL_RETURN_POLL_INTERVAL_MS = 2_000
const PORTAL_RETURN_POLL_ATTEMPTS = 30
const PAYMENT_COPY = {
  accessCheckError: "We couldn't check your access. Please try again.",
  catalogLoadError: "We couldn't load the plans. Please try again.",
  choosePlanTitle: "Choose a Plan",
  genericErrorTitle: "Something went wrong",
  invalidReturnNotice: "We couldn't finish that payment. Please try again.",
  managePlanError: "We couldn't open your plan page. Please try again.",
  noPlansDescription: "No plans are available right now. Please try again in a moment.",
  noPlansTitle: "No plans available",
  openCheckoutError: "We couldn't open the payment page. Please try again.",
} as const

type BillingGateProps = {
  enabled: boolean
}

const readCheckoutSessionIdFromSearch = (search: string) => {
  const query = new URLSearchParams(search)

  return query.get("session_id")?.trim() || query.get("checkout_session_id")?.trim() || ""
}

const hasBillingCancelFlag = (search: string) => {
  const query = new URLSearchParams(search)
  return query.get("billing_cancelled") === "1"
}

const hasBillingPortalReturnFlag = (search: string) => {
  const query = new URLSearchParams(search)
  return query.get(BILLING_PORTAL_RETURN_QUERY_KEY) === "1"
}

const readStoredCheckoutSessionId = () => {
  if (typeof window === "undefined") return ""
  return window.sessionStorage.getItem(STRIPE_CHECKOUT_SESSION_STORAGE_KEY)?.trim() || ""
}

const persistCheckoutSessionId = (sessionId: string) => {
  if (typeof window === "undefined") return
  if (!sessionId.trim()) return

  window.sessionStorage.setItem(STRIPE_CHECKOUT_SESSION_STORAGE_KEY, sessionId)
}

const clearStoredCheckoutSessionId = () => {
  if (typeof window === "undefined") return
  window.sessionStorage.removeItem(STRIPE_CHECKOUT_SESSION_STORAGE_KEY)
}

const wait = (delayMs: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, delayMs)
  })

const getCheckoutStatusErrorMessage = (
  status: Exclude<StripeCheckoutStatusState, "completed" | "open" | "processing">
) => {
  switch (status) {
    case "expired":
      return "This payment page expired. Please try again."
    case "failed":
      return "Your payment didn't go through. Please try again."
  }
}

const formatPrice = (amount: number, currency: string) => {
  return `${Math.round(amount / 100)} ${currency.toUpperCase()}`
}

const isTrialFeature = (feature: string) => /free trial/i.test(feature)

const getVisibleFeatures = (offer: StripeCatalogOffer, showTrialMessaging: boolean) => {
  return showTrialMessaging
    ? offer.features
    : offer.features.filter((feature) => !isTrialFeature(feature))
}

const getBillingNote = (offer: StripeCatalogOffer, showTrialMessaging: boolean) => {
  const priceLabel = formatPrice(offer.amount, offer.currency)
  const intervalLabel =
    offer.interval === "month" && offer.intervalCount === 1
      ? "every 30 days"
      : offer.intervalCount === 1
      ? `every ${offer.interval}`
      : `every ${offer.intervalCount} ${offer.interval}s`

  const firstLine = showTrialMessaging
    ? `After the trial, you'll pay ${priceLabel} ${intervalLabel} until you cancel.`
    : `You'll pay ${priceLabel} ${intervalLabel} until you cancel.`

  return `${firstLine} You can cancel anytime.`
}

const readCheckoutStatusError = (error: unknown) => {
  if (!error || typeof error !== "object") return null

  const record = error as Record<string, unknown>
  const status = typeof record.status === "number" ? record.status : null
  const data =
    record.data && typeof record.data === "object" && !Array.isArray(record.data)
      ? (record.data as Record<string, unknown>)
      : null
  const code = data && typeof data.code === "string" ? data.code : ""

  return {
    code,
    status,
  }
}

const isInvalidCheckoutReturnError = (error: unknown) => {
  const checkoutError = readCheckoutStatusError(error)
  if (!checkoutError) return false

  return checkoutError.status === 404 || checkoutError.code === "checkout_session_not_found"
}

export const BillingGate: React.FC<BillingGateProps> = ({ enabled }) => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const entitlement = useAppSelector(selectBillingEntitlement)
  const isPaywallOpen = useAppSelector(selectBillingPaywallOpen)
  const portalUrl = useAppSelector(selectBillingPortalUrl)

  const [catalogOffers, setCatalogOffers] = useState<StripeCatalogOffer[]>([])
  const [isTrialEligible, setIsTrialEligible] = useState(true)
  const [catalogStatus, setCatalogStatus] = useState<BillingCatalogStatus>("idle")
  const [isPortalSessionLoading, setIsPortalSessionLoading] = useState(false)
  const [activeCheckoutPriceId, setActiveCheckoutPriceId] = useState<string | null>(null)
  const [checkoutSessionId, setCheckoutSessionId] = useState<string | null>(null)
  const [, setCheckoutSyncState] = useState<StripeCheckoutStatusState | null>(null)
  const [isPortalReturnSyncing, setIsPortalReturnSyncing] = useState(false)
  const [, setCheckoutStatusNotice] = useState("")
  const [errorMessage, setErrorMessage] = useState("")
  const paywallViewedRef = useRef(false)

  const shouldRenderGate = enabled
  const currentSearch = searchParams.toString()
  const locationSearch = currentSearch ? `?${currentSearch}` : ""
  const currentReturnPath = `${location.pathname}${location.search}`
  const storedCheckoutSessionId = readStoredCheckoutSessionId()
  const hasCheckoutReturnSession = Boolean(readCheckoutSessionIdFromSearch(locationSearch))
  const hasPortalReturn = hasBillingPortalReturnFlag(locationSearch)
  const requiresPortal = entitlement?.requiresPortal ?? false
  const isTrialCanceled = entitlement?.lockReason === "trial_canceled"
  const showTrialMessaging = isTrialEligible && !requiresPortal && !isTrialCanceled
  const isCheckoutStatusPolling = Boolean(checkoutSessionId)
  const isExternalBillingFlowActive = Boolean(
    checkoutSessionId ||
      storedCheckoutSessionId ||
      hasCheckoutReturnSession ||
      hasPortalReturn ||
      isPortalReturnSyncing
  )
  const isCatalogLoading = catalogStatus === "loading"
  const needsCatalogBeforeOpen =
    shouldRenderGate && isPaywallOpen && !requiresPortal && !isCheckoutStatusPolling
  const isPreparingPaywall =
    needsCatalogBeforeOpen && (catalogStatus === "idle" || catalogStatus === "loading")
  const isPaywallVisible = isPaywallOpen && !isPreparingPaywall

  const resetCatalogState = useCallback(() => {
    setCatalogOffers([])
    setCatalogStatus("idle")
    setIsTrialEligible(true)
    setActiveCheckoutPriceId(null)
  }, [])

  const clearStripeReturnParams = useCallback(() => {
    const nextSearchParams = new URLSearchParams(searchParams)
    let changed = false

    STRIPE_RETURN_QUERY_KEYS.forEach((key) => {
      if (!nextSearchParams.has(key)) return
      nextSearchParams.delete(key)
      changed = true
    })

    if (!changed) return
    setSearchParams(nextSearchParams, {
      replace: true,
    })
  }, [searchParams, setSearchParams])

  const resetCheckoutReturnState = useCallback(
    (input?: {
      errorMessage?: string
      notice?: string
      syncState?: StripeCheckoutStatusState | null
    }) => {
      clearStoredCheckoutSessionId()
      clearStripeReturnParams()
      setIsPortalReturnSyncing(false)
      setCheckoutSessionId(null)
      setCheckoutSyncState(input?.syncState ?? null)
      setCheckoutStatusNotice(input?.notice ?? "")

      if (input && "errorMessage" in input) setErrorMessage(input.errorMessage ?? "")
    },
    [clearStripeReturnParams]
  )

  const refreshEntitlement = useCallback(
    async (autoOpenPaywall: boolean) => {
      if (!shouldRenderGate) {
        dispatch(closeBillingPaywall())
        return
      }

      try {
        const nextEntitlement = await billingApi.stripeEntitlement()
        setErrorMessage("")

        if (nextEntitlement.entitled) {
          resetCheckoutReturnState({
            errorMessage: "",
            notice: "",
            syncState: "completed",
          })
          dispatch(closeBillingPaywall())
          return
        }

        if (autoOpenPaywall) dispatch(openBillingPaywall())
      } catch (error) {
        if (autoOpenPaywall) dispatch(openBillingPaywall())
        setErrorMessage(PAYMENT_COPY.accessCheckError)
      }
    },
    [dispatch, resetCheckoutReturnState, shouldRenderGate]
  )

  const recoverInvalidCheckoutReturnState = useCallback(async () => {
    resetCheckoutReturnState({
      errorMessage: "",
      notice: PAYMENT_COPY.invalidReturnNotice,
      syncState: null,
    })
    dispatch(openBillingPaywall())
    await refreshEntitlement(false)
  }, [dispatch, refreshEntitlement, resetCheckoutReturnState])

  const startPortalSession = useCallback(async () => {
    if (!shouldRenderGate) return

    try {
      setIsPortalSessionLoading(true)
      setErrorMessage("")

      let nextPortalUrl = portalUrl
      if (!nextPortalUrl) {
        const session = await billingApi.stripePortalSession({
          returnPath: currentReturnPath,
        })
        nextPortalUrl = session.url
        dispatch(setBillingPortalUrl(nextPortalUrl))
      }

      if (!nextPortalUrl) {
        setErrorMessage(PAYMENT_COPY.managePlanError)
        return
      }

      dispatch(closeBillingPaywall())
      window.location.assign(nextPortalUrl)
    } catch (error) {
      setErrorMessage(PAYMENT_COPY.managePlanError)
    } finally {
      setIsPortalSessionLoading(false)
    }
  }, [currentReturnPath, dispatch, portalUrl, shouldRenderGate])

  const startCheckoutSession = useCallback(
    async (offer: StripeCatalogOffer) => {
      try {
        setActiveCheckoutPriceId(offer.priceId)
        setErrorMessage("")
        setCheckoutStatusNotice("")
        capturePlatformEvent("pricing_plan_selected", {
          billing_interval: offer.interval,
          billing_interval_count: offer.intervalCount,
          currency: offer.currency,
          price_amount: offer.amount,
          price_id: offer.priceId,
          product_id: offer.productId,
          trial_eligible: showTrialMessaging,
        })

        const result = await billingApi.stripeCheckoutSession(offer.priceId, {
          returnPath: currentReturnPath,
        })
        if (result.ok) {
          capturePlatformEvent("stripe_checkout_accepted", {
            billing_interval: offer.interval,
            billing_interval_count: offer.intervalCount,
            price_id: offer.priceId,
            product_id: offer.productId,
            stripe_checkout_session_id: result.data.sessionId,
            trial_eligible: showTrialMessaging,
          })
          persistCheckoutSessionId(result.data.sessionId)
          setCheckoutSessionId(result.data.sessionId)
          dispatch(closeBillingPaywall())
          window.location.assign(result.data.url)
          return
        }

        const errorData = result.data as StripeErrorResponse | null
        if (errorData?.code === "billing_already_active") {
          if (errorData.entitlement) dispatch(setBillingEntitlement(errorData.entitlement))
          if (errorData.portalUrl) dispatch(setBillingPortalUrl(errorData.portalUrl))

          if (errorData.entitlement?.entitled) {
            resetCheckoutReturnState({
              errorMessage: "",
              notice: "",
              syncState: "completed",
            })
            dispatch(closeBillingPaywall())
          }

          return
        }

        if (errorData?.code === "billing_portal_required") return

        setErrorMessage(PAYMENT_COPY.openCheckoutError)
        return
      } catch (error) {
        setErrorMessage(PAYMENT_COPY.openCheckoutError)
      } finally {
        setActiveCheckoutPriceId(null)
      }
    },
    [currentReturnPath, dispatch, resetCheckoutReturnState, showTrialMessaging]
  )

  useEffect(() => {
    if (!shouldRenderGate) {
      setErrorMessage("")
      setCheckoutStatusNotice("")
      resetCheckoutReturnState({
        errorMessage: "",
        notice: "",
        syncState: null,
      })
      resetCatalogState()
      dispatch(closeBillingPaywall())
      return
    }

    if (entitlement?.entitled) {
      dispatch(closeBillingPaywall())
      return
    }

    if (entitlement && !entitlement.entitled) {
      if (isExternalBillingFlowActive) return
      dispatch(openBillingPaywall())
      return
    }

    void refreshEntitlement(!isExternalBillingFlowActive)
  }, [
    dispatch,
    entitlement,
    isExternalBillingFlowActive,
    refreshEntitlement,
    resetCatalogState,
    resetCheckoutReturnState,
    shouldRenderGate,
  ])

  useEffect(() => {
    if (!shouldRenderGate) return

    if (hasBillingCancelFlag(locationSearch)) {
      resetCheckoutReturnState({
        errorMessage: "",
        notice: "Payment was canceled. You can try again anytime.",
        syncState: null,
      })
      dispatch(openBillingPaywall())
      return
    }

    if (hasCheckoutReturnSession) {
      const returnSessionId = readCheckoutSessionIdFromSearch(locationSearch)
      persistCheckoutSessionId(returnSessionId)
      setCheckoutSessionId(returnSessionId || null)
      setCheckoutSyncState("processing")
      setIsPortalReturnSyncing(false)
      setCheckoutStatusNotice("")
      setErrorMessage("")
      return
    }

    if (hasPortalReturn) {
      if (entitlement?.entitled) {
        resetCheckoutReturnState({
          errorMessage: "",
          notice: "",
          syncState: "completed",
        })
        dispatch(closeBillingPaywall())
        return
      }

      setCheckoutSessionId(null)
      setCheckoutSyncState(null)
      setIsPortalReturnSyncing(true)
      setCheckoutStatusNotice("We're syncing your payment status. This can take a moment.")
      setErrorMessage("")
      return
    }

    if (!storedCheckoutSessionId || entitlement?.entitled) return

    setCheckoutSessionId((currentSessionId) => currentSessionId ?? storedCheckoutSessionId)
  }, [
    dispatch,
    entitlement?.entitled,
    hasCheckoutReturnSession,
    hasPortalReturn,
    locationSearch,
    resetCheckoutReturnState,
    shouldRenderGate,
    storedCheckoutSessionId,
  ])

  useEffect(() => {
    if (!isPaywallVisible) {
      paywallViewedRef.current = false
      return
    }

    if (paywallViewedRef.current) return
    paywallViewedRef.current = true

    capturePlatformEvent("paywall_viewed", {
      access_state: entitlement?.accessState,
      entitlement_status: entitlement?.entitlementStatus,
      lock_reason: entitlement?.lockReason,
      requires_portal: requiresPortal,
      trial_eligible: isTrialEligible,
    })
  }, [
    entitlement?.accessState,
    entitlement?.entitlementStatus,
    entitlement?.lockReason,
    isPaywallVisible,
    isTrialEligible,
    requiresPortal,
  ])

  useEffect(() => {
    if (!shouldRenderGate || !isPortalReturnSyncing || entitlement?.entitled) return

    let canceled = false

    const syncPortalReturn = async () => {
      setCheckoutStatusNotice("We're syncing your payment status. This can take a moment.")
      setErrorMessage("")

      try {
        for (let attempt = 0; attempt < PORTAL_RETURN_POLL_ATTEMPTS; attempt += 1) {
          if (attempt > 0) await wait(PORTAL_RETURN_POLL_INTERVAL_MS)
          if (canceled) return

          const nextEntitlement = await billingApi.stripeEntitlement({
            suppressPaywall: true,
          })
          if (canceled) return

          if (nextEntitlement.entitled) {
            resetCheckoutReturnState({
              errorMessage: "",
              notice: "",
              syncState: "completed",
            })
            dispatch(closeBillingPaywall())
            return
          }
        }

        if (canceled) return

        resetCheckoutReturnState({
          errorMessage: "",
          notice: "We're still syncing your payment status. Your access will update soon.",
          syncState: null,
        })
      } catch (error) {
        if (canceled) return

        resetCheckoutReturnState({
          errorMessage: "",
          notice: "We're still syncing your payment status. Your access will update soon.",
          syncState: null,
        })
      }
    }

    void syncPortalReturn()

    return () => {
      canceled = true
    }
  }, [
    dispatch,
    entitlement?.entitled,
    isPortalReturnSyncing,
    resetCheckoutReturnState,
    shouldRenderGate,
  ])

  useEffect(() => {
    if (!shouldRenderGate || !checkoutSessionId || entitlement?.entitled) return

    let canceled = false

    const syncCheckoutStatus = async () => {
      setCheckoutSyncState("processing")
      setCheckoutStatusNotice("We're checking your payment. This can take a moment.")
      setErrorMessage("")

      try {
        let lastKnownStatus: StripeCheckoutStatusState = "processing"

        for (const delayMs of CHECKOUT_STATUS_POLL_DELAYS_MS) {
          if (delayMs > 0) await wait(delayMs)
          if (canceled) return

          const nextStatus = await billingApi.stripeCheckoutStatus(checkoutSessionId, {
            suppressPaywall: true,
          })
          if (canceled) return

          lastKnownStatus = nextStatus.status
          setCheckoutSyncState(nextStatus.status)

          if (nextStatus.status === "completed") {
            resetCheckoutReturnState({
              errorMessage: "",
              notice: "",
              syncState: "completed",
            })
            dispatch(closeBillingPaywall())
            return
          }

          if (nextStatus.status !== "processing") {
            if (nextStatus.status === "open") {
              resetCheckoutReturnState({
                errorMessage: "",
                notice: "That payment page is no longer available. Please try again.",
                syncState: nextStatus.status,
              })
              dispatch(openBillingPaywall())
              return
            }

            resetCheckoutReturnState({
              errorMessage: getCheckoutStatusErrorMessage(nextStatus.status),
              notice: "",
              syncState: nextStatus.status,
            })
            dispatch(openBillingPaywall())
            return
          }
        }

        if (canceled || lastKnownStatus !== "processing") return

        resetCheckoutReturnState({
          errorMessage: "",
          notice: "We're still checking your payment. Your access will update soon.",
          syncState: "processing",
        })
      } catch (error) {
        if (canceled) return

        if (isInvalidCheckoutReturnError(error)) {
          await recoverInvalidCheckoutReturnState()
          return
        }

        setCheckoutSyncState("failed")
        setCheckoutStatusNotice("")
        setErrorMessage("We couldn't check your payment yet. Please try again.")
      }
    }

    void syncCheckoutStatus()

    return () => {
      canceled = true
    }
  }, [
    checkoutSessionId,
    dispatch,
    entitlement?.entitled,
    recoverInvalidCheckoutReturnState,
    resetCheckoutReturnState,
    shouldRenderGate,
  ])

  useEffect(() => {
    if (!isPaywallOpen || !shouldRenderGate) return
    if (requiresPortal) {
      setCatalogStatus("idle")
      return
    }
    if (catalogStatus === "ready") return

    let canceled = false

    const loadCatalog = async () => {
      setErrorMessage("")
      resetCatalogState()
      setCatalogStatus("loading")

      try {
        const catalog = await billingApi.stripeCatalog()
        if (canceled) return

        setCatalogOffers(catalog.offers)
        setCatalogStatus("ready")
        setIsTrialEligible(!isTrialCanceled && catalog.trialEligible)
      } catch (error) {
        if (canceled) return

        setCatalogStatus("error")
        setErrorMessage(PAYMENT_COPY.catalogLoadError)
      }
    }

    void loadCatalog()

    return () => {
      canceled = true
    }
  }, [isPaywallOpen, isTrialCanceled, requiresPortal, resetCatalogState, shouldRenderGate])

  if (!shouldRenderGate) return null

  return (
    <>
      <Dialog
        open={isPaywallVisible}
        onOpenChange={(open) => {
          if (open) dispatch(openBillingPaywall())
          else dispatch(closeBillingPaywall())
        }}
      >
        <DialogContent
          showClose={false}
          className="flex max-h-[calc(100dvh-1rem)] w-[96vw] overflow-hidden border-0 bg-transparent p-0 shadow-none sm:max-h-[calc(100dvh-1.5rem)] sm:max-w-[72rem]"
        >
          <div className="flex max-h-full min-h-0 w-full flex-col overflow-hidden rounded-3xl bg-background px-4 py-4 shadow-2xl sm:px-5 sm:py-5">
            <DialogHeader className="mb-4 shrink-0 flex-row items-start justify-between gap-4 border-b pb-4 text-left">
              <DialogTitle>{PAYMENT_COPY.choosePlanTitle}</DialogTitle>
              <DialogClose className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </DialogClose>
            </DialogHeader>

            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain pr-1">
              {!isCatalogLoading && requiresPortal && (
                <Card className="rounded-2xl border-border/70 shadow-lg">
                  <CardHeader className="flex flex-col gap-3">
                    <Alert variant="destructive">
                      <AlertTriangle className="size-4" />
                      <AlertTitle>Action needed</AlertTitle>
                      <AlertDescription>
                        Please update your payment details before you continue.
                      </AlertDescription>
                    </Alert>
                    <CardDescription className="leading-6">
                      Open the plan page, make the needed changes, and come back here.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="grid grid-cols-[repeat(auto-fit,minmax(13rem,1fr))] gap-3 text-sm text-muted-foreground">
                    <Card className="shadow-none">
                      <CardHeader className="gap-1 p-4">
                        <CardTitle className="text-sm">Payment issue</CardTitle>
                        <CardDescription>Please update your payment details.</CardDescription>
                      </CardHeader>
                    </Card>
                    <Card className="shadow-none">
                      <CardHeader className="gap-1 p-4">
                        <CardTitle className="text-sm">Plan paused</CardTitle>
                        <CardDescription>
                          Turn your plan back on to keep using the app.
                        </CardDescription>
                      </CardHeader>
                    </Card>
                    <Card className="shadow-none">
                      <CardHeader className="gap-1 p-4">
                        <CardTitle className="text-sm">One more step</CardTitle>
                        <CardDescription>
                          Please confirm your payment or update your details.
                        </CardDescription>
                      </CardHeader>
                    </Card>
                  </CardContent>
                  <CardFooter className="flex items-center gap-3">
                    <Button
                      type="button"
                      onClick={() => void startPortalSession()}
                      disabled={isPortalSessionLoading}
                    >
                      {isPortalSessionLoading && (
                        <Loader2 data-icon="inline-start" className="animate-spin" />
                      )}
                      Open plan page
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => dispatch(closeBillingPaywall())}
                    >
                      Close
                    </Button>
                  </CardFooter>
                </Card>
              )}

              {!isCatalogLoading && !requiresPortal && catalogOffers.length > 0 && (
                <div className="grid gap-5 md:grid-cols-3">
                  {catalogOffers.map((offer) => {
                    const isCheckingOut = activeCheckoutPriceId === offer.priceId
                    const visibleFeatures = getVisibleFeatures(offer, showTrialMessaging)
                    const billingNote = getBillingNote(offer, showTrialMessaging)

                    return (
                      <Card
                        key={offer.priceId}
                        className={cn(
                          "flex h-full min-h-[24rem] flex-col overflow-hidden border-border/70 shadow-sm",
                          offer.highlight ? "border-emerald-500 shadow-lg shadow-emerald-100" : ""
                        )}
                      >
                        <CardHeader className="flex flex-col gap-3 border-b bg-muted/20 pb-3">
                          <div className="flex items-center gap-3">
                            <CardTitle className="text-2xl leading-none">{offer.name}</CardTitle>
                            {offer.highlight && (
                              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                                Most Popular
                              </Badge>
                            )}
                            {isTrialCanceled && !offer.highlight && (
                              <Badge variant="outline">No free trial</Badge>
                            )}
                          </div>

                          <CardDescription className="leading-6">
                            {offer.description}
                          </CardDescription>

                          <div className="flex items-baseline gap-1.5">
                            <div className="text-xl font-semibold tracking-tight">
                              {formatPrice(offer.amount, offer.currency)}
                            </div>
                            <div className="text-sm text-muted-foreground">/month</div>
                          </div>
                        </CardHeader>

                        <CardContent className="flex flex-1 flex-col gap-5 py-5">
                          <div className="flex flex-1 flex-col gap-3">
                            {visibleFeatures.map((feature) => (
                              <div key={feature} className="flex items-start gap-3 text-sm">
                                <div className="mt-2 size-3 shrink-0 rounded-full bg-emerald-500" />
                                <span className="leading-6 text-foreground/80">{feature}</span>
                              </div>
                            ))}
                          </div>

                          <p className="mt-auto text-sm leading-7 text-muted-foreground">
                            {billingNote}
                          </p>
                        </CardContent>

                        <CardFooter className="pt-0">
                          <Button
                            type="button"
                            className={cn(
                              "w-full",
                              offer.highlight
                                ? "bg-emerald-600 text-white hover:bg-emerald-700"
                                : "bg-foreground text-background hover:bg-foreground/90"
                            )}
                            onClick={() => void startCheckoutSession(offer)}
                            disabled={isCheckingOut}
                          >
                            {isCheckingOut && (
                              <Loader2 data-icon="inline-start" className="animate-spin" />
                            )}
                            {showTrialMessaging ? "Start Trial" : "Choose plan"}
                          </Button>
                        </CardFooter>
                      </Card>
                    )
                  })}
                </div>
              )}

              {!isCatalogLoading &&
                !requiresPortal &&
                catalogOffers.length === 0 &&
                !errorMessage && (
                  <Alert>
                    <AlertTitle>{PAYMENT_COPY.noPlansTitle}</AlertTitle>
                    <AlertDescription>{PAYMENT_COPY.noPlansDescription}</AlertDescription>
                  </Alert>
                )}

              {errorMessage && (
                <Alert variant="destructive">
                  <AlertTriangle className="size-4" />
                  <AlertTitle>{PAYMENT_COPY.genericErrorTitle}</AlertTitle>
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
